'use client';

import { c as _c } from "react/compiler-runtime";
import { BorderBeam, type BorderBeamPalette } from 'border-beam';
import { m, useInView, useReducedMotion } from 'motion/react';
import { type PointerEvent, type ReactNode, useRef, useState } from 'react';
import { outcomeHueToVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useAppliedTheme } from '@/hooks/ui/use-applied-theme';
import { EASE_POSITION_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { OutcomeRowSummary } from './outcome-row-summary';

// Two beam cycles give the background a slower drift than the border.
const GLOW_DRIFT = {
  duration: 4.6,
  ease: EASE_POSITION_MOTION,
  repeat: Infinity,
  repeatType: 'mirror'
} as const;
export function MarketOutcomeHover(t0) {
  const $ = _c(62);
  const {
    marketPubkey,
    outcomes,
    winnerOriginalIndex,
    children
  } = t0;
  const theme = useAppliedTheme();
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref);
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== outcomes) {
    t1 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== marketPubkey) {
    t2 = {
      marketPubkey
    };
    $[2] = marketPubkey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const colors = useOutcomeColor(t1, t2);
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== children || $[5] !== colors || $[6] !== hovered || $[7] !== inView || $[8] !== outcomes || $[9] !== reduceMotion || $[10] !== theme || $[11] !== winnerOriginalIndex) {
    t21 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t22;
      if ($[32] !== colors) {
        t22 = (o_0, i) => [o_0.originalIndex, colors[i]];
        $[32] = colors;
        $[33] = t22;
      } else {
        t22 = $[33];
      }
      const colorByIndex = new Map(outcomes.map(t22));
      const visible = [...outcomes].sort(_temp2).slice(0, 2).map(outcome => {
        const color = colorByIndex.get(outcome.originalIndex);
        const winner = winnerOriginalIndex === outcome.originalIndex;
        const resolved = winnerOriginalIndex !== undefined;
        return {
          index: outcome.originalIndex,
          ink: resolved ? winner ? "var(--color-feedback-success-content)" : "var(--color-background-tertiary)" : color === undefined ? undefined : outcomeHueToVar(color.hue, "text"),
          fill: resolved ? winner ? "bg-feedback-success-content" : "bg-background-tertiary" : cn("bg-current", color?.text)
        };
      });
      const first = visible[0]?.ink;
      const second = visible[1]?.ink;
      if (first === undefined || second === undefined) {
        t21 = children;
        break bb0;
      }
      const moving = hovered && inView && !reduceMotion;
      T0 = BorderBeam;
      t7 = ref;
      t8 = "pulse-inner";
      t9 = hoverPalette(first, second);
      t10 = true;
      t11 = theme ?? "dark";
      t12 = 20;
      t13 = hovered && !reduceMotion;
      t14 = 1;
      t15 = 1;
      t16 = "relative h-full rounded-card";
      t17 = "market-outcome-hover";
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = event => {
          if (event.pointerType === "mouse") {
            setHovered(true);
          }
        };
        t19 = () => setHovered(false);
        $[34] = t18;
        $[35] = t19;
      } else {
        t18 = $[34];
        t19 = $[35];
      }
      t20 = children;
      t3 = "true";
      t4 = "market-outcome-glow";
      const t23 = hovered ? "opacity-15" : "opacity-0";
      if ($[36] !== t23) {
        t5 = cn("pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-card transition-opacity duration-slow ease-default motion-reduce:transition-none", t23);
        $[36] = t23;
        $[37] = t5;
      } else {
        t5 = $[37];
      }
      let t24;
      if ($[38] !== moving) {
        t24 = (color_0, index) => <m.div key={color_0.index} data-outcome-glow={color_0.index} className={cn("absolute h-full w-3/4 rounded-full blur-3xl", index === 0 ? "-left-8 -top-8" : "-right-8 -bottom-8", color_0.fill)} initial={false} animate={moving ? {
          x: index === 0 ? [0, 24] : [0, -24],
          y: [0, 16],
          scale: [1, 1.15]
        } : {
          x: 0,
          y: 0,
          scale: 1
        }} transition={moving ? GLOW_DRIFT : {
          duration: 0
        }} />;
        $[38] = moving;
        $[39] = t24;
      } else {
        t24 = $[39];
      }
      t6 = visible.map(t24);
    }
    $[4] = children;
    $[5] = colors;
    $[6] = hovered;
    $[7] = inView;
    $[8] = outcomes;
    $[9] = reduceMotion;
    $[10] = theme;
    $[11] = winnerOriginalIndex;
    $[12] = T0;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
    $[22] = t19;
    $[23] = t20;
    $[24] = t21;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t14 = $[17];
    t15 = $[18];
    t16 = $[19];
    t17 = $[20];
    t18 = $[21];
    t19 = $[22];
    t20 = $[23];
    t21 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  if (t21 !== Symbol.for("react.early_return_sentinel")) {
    return t21;
  }
  let t22;
  if ($[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t22 = <div aria-hidden={t3} data-testid={t4} className={t5}>{t6}</div>;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== T0 || $[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17 || $[54] !== t18 || $[55] !== t19 || $[56] !== t20 || $[57] !== t22 || $[58] !== t7 || $[59] !== t8 || $[60] !== t9) {
    t23 = <T0 ref={t7} size={t8} colorVariant={t9} staticColors={t10} theme={t11} borderRadius={t12} active={t13} brightness={t14} saturation={t15} className={t16} data-testid={t17} onPointerEnter={t18} onPointerLeave={t19}>{t20}{t22}</T0>;
    $[45] = T0;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t22;
    $[58] = t7;
    $[59] = t8;
    $[60] = t9;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  return t23;
}
function _temp2(a, b) {
  return b.share - a.share || a.originalIndex - b.originalIndex;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.originalIndex
  };
}
function hoverPalette(first: string, second: string): BorderBeamPalette {
  return {
    border: [{
      color: first,
      pos: '33% -7.4%',
      size: '70px 40px'
    }, {
      color: first,
      pos: '12% -5%',
      size: '60px 35px'
    }, {
      color: first,
      pos: '2.1% 68.3%',
      size: '40px 70px'
    }, {
      color: first,
      pos: '2.1% 68.3%',
      size: '20px 35px'
    }, {
      color: second,
      pos: '74.4% 100%',
      size: '180px 32px'
    }, {
      color: second,
      pos: '55% 100%',
      size: '85px 26px'
    }, {
      color: second,
      pos: '93.9% 0%',
      size: '74px 32px'
    }, {
      color: second,
      pos: '100% 27.1%',
      size: '26px 42px'
    }, {
      color: second,
      pos: '100% 27.1%',
      size: '52px 48px'
    }]
  };
}